import {
  buildShortenVersion,
  getMaxWordCountForInstantAnswerExplanation,
} from "@faire/help-center-utils/lib/textHelper";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { trackCxFoundationsInstantanswerlearnmoreClick } from "@faire/web-api--source/events/cxFoundations/click/instantanswerlearnmore";
import kebabCase from "lodash/kebabCase";
import React, { useState, type JSX } from "react";

import { ShowMoreCta } from "./ShowMoreCta";
import { contentIsShorterThanMax } from "./utils";

interface IProps {
  descriptionParagraphs: string[] | JSX.Element;
  viewHasHelpCenterArticles: boolean;
}

export const InstantAnswerExplanation: React.FC<IProps> = ({
  descriptionParagraphs,
  viewHasHelpCenterArticles,
}) => {
  const [userClickedOnShowMore, setUserClickedOnShowMore] = useState(false);
  const { isTabletAndAbove } = useViewport();
  const maxVisibleDescriptionWords = getMaxWordCountForInstantAnswerExplanation(
    viewHasHelpCenterArticles
  );
  const retailerToken = getRetailerToken();
  const brandToken = getBrandToken();

  if (Array.isArray(descriptionParagraphs)) {
    if (descriptionParagraphs.length === 0) {
      return null;
    }

    const showFullExplanation =
      userClickedOnShowMore ||
      isTabletAndAbove ||
      !viewHasHelpCenterArticles ||
      contentIsShorterThanMax(
        descriptionParagraphs,
        maxVisibleDescriptionWords
      );

    if (showFullExplanation) {
      return (
        <>
          {descriptionParagraphs.map((description, index) => (
            <div key={index}>
              {index > 0 ? <Spacer height="2x" /> : null}
              <Typography data-test-id={`${kebabCase(description)}-des`}>
                {description}
              </Typography>
            </div>
          ))}
        </>
      );
    }

    const firstParagraph = descriptionParagraphs[0]!;
    const shortenFirstParagraph = buildShortenVersion(
      firstParagraph,
      maxVisibleDescriptionWords
    );
    const thereIsMoreContentToShow =
      shortenFirstParagraph.length - "...".length <= firstParagraph.length ||
      descriptionParagraphs.length > 1;

    const showMoreCtaOnClickHandler = () => {
      setUserClickedOnShowMore(true);
      trackCxFoundationsInstantanswerlearnmoreClick(
        retailerToken ?? "",
        brandToken ?? ""
      );
    };

    return (
      <div>
        <Typography data-test-id="instant-answer-cut-description">
          {shortenFirstParagraph}
          <ShowMoreCta
            visibleWhen={thereIsMoreContentToShow}
            onClick={showMoreCtaOnClickHandler}
          />
        </Typography>
      </div>
    );
  }

  return <>{descriptionParagraphs}</>;
};
